'use client';

/**
 * LexInfoMenu — the "how does @Lex work here?" affordance for group chat.
 *
 * In a group session the AI is opt-in: it answers only when a message mentions
 * @Lex. That rule is invisible, and a member who does not know it reads the
 * silence as the feature being broken. The entry banner
 * (`GroupChatIntroBanner`) teaches the rule once; this dropdown is the durable
 * place to re-read it afterwards, which is why it lives in the persistent
 * presence cluster next to the member avatars rather than in dismissible copy.
 *
 * Presentational and self-contained: it holds no session state and takes no
 * props, so it can sit anywhere the cluster is rendered.
 */

import { useTranslations } from 'next-intl';
import type { ReactElement } from 'react';

import { LexAvatar } from '@/components/chat/LexAvatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function LexInfoMenu(): ReactElement {
  const t = useTranslations('LexInfoMenu');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t('triggerLabel')}
        // 44px hit area via padding while the avatar itself stays visually 28px,
        // so the touch target meets WCAG 2.5.5 without inflating the cluster.
        className='flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-interactive-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tertiary'
      >
        <LexAvatar className='h-7 w-7' />
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-72'>
        <div className='flex items-start gap-3 p-3'>
          <LexAvatar className='h-8 w-8 flex-shrink-0' />
          <div className='space-y-1'>
            <p className='text-sm font-semibold text-content-primary'>
              {t('title')}
            </p>
            <p className='text-xs leading-relaxed text-content-secondary'>
              {t('description')}
            </p>
            <p className='text-xs leading-relaxed text-content-muted'>
              {t('hint')}
            </p>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
